<script setup lang="ts">
import { computed, Ref, ref, toRefs } from "vue";
import { titleInputRules } from "@/ui/validation/validationRules";
import { useI18n } from "vue-i18n";
import { useApiClient } from "@/api/client";
import { toastApiResult } from "@/toaster";
import { isSharedWithMeNode } from "@/ui/components/FileBrowser/types";
const { t } = useI18n();
const isFormValid: Ref<boolean> = ref(false);

const isDialogVisible = defineModel<boolean>();

const props = defineProps<{
	nodes: App.Data.ResourceTreeNodeDO[];
	folderToEdit: App.Data.ResourceTreeNodeDO;
	resourceTreeCategory?: "requested_documents" | "dashboards";
}>();

const { nodes, folderToEdit } = toRefs(props);

const visibleNodes = computed(() =>
	nodes.value.filter(
		(folder) =>
			folder.id !== folderToEdit.value?.id && isSharedWithMeNode(folder)
	)
);

const parentFolder = computed(() => {
	return nodes.value.filter(
		(folder: App.Data.ResourceTreeNodeDO) =>
			folder.id === folderToEdit.value?.parent_id
	)[0];
});

const closeModal = () => {
	isDialogVisible.value = false;
};

const createFolder = async () => {
	await toastApiResult(
		useApiClient("resourceTreeNodes").createResourceTreeNode(folderToEdit.value)
	);
};

const updateFolder = async () => {
	await toastApiResult(
		useApiClient("resourceTreeNodes").updateResourceTreeNode(folderToEdit.value)
	);
};

const saveFolder = async () => {
	if (!folderToEdit.value?.id) {
		await createFolder();
	} else {
		await updateFolder();
	}
	closeModal();
};

const cardTitle = computed(() => {
	return folderToEdit.value?.id
		? t("fileBrowser.folderDialog.updateTitle")
		: `${t("fileBrowser.folderDialog.createTitle")} to ${
				parentFolder.value?.name ?? "Root"
		  }`;
});
</script>

<template>
	<v-dialog
		v-model="isDialogVisible"
		scrim="#000000"
		overlay-color="black"
		max-width="50vw"
	>
		<v-form v-model="isFormValid">
			<v-card>
				<v-toolbar>
					<template v-slot:prepend>
						<v-icon start class="ml-3 mr-5">mdi-folder-plus-outline</v-icon>
						<v-toolbar-title class="pt-1">
							{{ cardTitle }}
						</v-toolbar-title>
					</template>
				</v-toolbar>
				<v-divider />

				<v-row v-if="nodes.length > 0">
					<v-col cols="12">
						<v-card-text class="d-flex flex-column ga-2">
							<v-select
								v-if="folderToEdit.id"
								v-model="folderToEdit.parent_id"
								:label="t('fileBrowser.folderDialog.parentFolder')"
								:items="visibleNodes"
								item-title="name"
								item-value="id"
								hide-details
							/>

							<v-text-field
								v-model="folderToEdit.name"
								:rules="titleInputRules(t)"
								:label="t('fileBrowser.folderDialog.name')"
								:placeholder="t('fileBrowser.folderDialog.name')"
								clearable
								hide-details
							/>
						</v-card-text>
					</v-col>
				</v-row>

				<v-divider style="border-color: unset !important"></v-divider>

				<v-card-actions class="mx-5 my-2">
					<v-spacer></v-spacer>
					<v-btn
						:text="t('fileBrowser.actions.close')"
						variant="text"
						@click="closeModal"
					></v-btn>
					<v-btn
						append-icon="mdi-content-save"
						color="primary"
						:text="t('fileBrowser.actions.save')"
						variant="flat"
						:disabled="isFormValid === false"
						@click="saveFolder"
					></v-btn>
				</v-card-actions>
			</v-card>
		</v-form>
	</v-dialog>
</template>

<style lang="scss" scoped></style>
